import React, { useEffect, useState } from 'react';
import { FiFileText } from 'react-icons/fi';
import { getContactLeadForms, type LeadFormSubmission } from '../../services/CardService';
import './crmFunnel.css';

interface Props {
  sectorId: number;
  contactId: number;
  /** "dossier": dentro do cartão "Cliente e contexto" da ficha completa. */
  variant?: 'panel' | 'dossier';
}

type State =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ok'; items: LeadFormSubmission[] };

function formatWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/** Linha "{origem} · {detalhe}[ · Campanha {id}] · {data}": a campanha só aparece quando o envio trouxe uma. */
function sourceLine(item: LeadFormSubmission): string {
  const parts = [item.originLabel];
  if (item.detail) parts.push(item.detail);
  if (item.campaignId) parts.push(`Campanha ${item.campaignId}`);
  const when = formatWhen(item.receivedAtUtc);
  if (when) parts.push(when);
  return parts.join(' · ');
}

/** Todas as respostas do formulário do contato, com rótulo e valor como o lead enviou (sem corte nem interpretação). */
const LeadFormAnswers: React.FC<Props> = ({ sectorId, contactId, variant = 'panel' }) => {
  const [state, setState] = useState<State>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    (async () => {
      try {
        const items = await getContactLeadForms(sectorId, contactId);
        if (!cancelled) setState({ status: 'ok', items: Array.isArray(items) ? items : [] });
      } catch {
        if (!cancelled) setState({ status: 'error' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sectorId, contactId]);

  if (state.status === 'loading') return null;

  const className = variant === 'dossier' ? 'lead-forms lead-forms--dossier' : 'opp-panel__section lead-forms';

  if (state.status === 'error') {
    return (
      <section className={className} data-testid="lead-forms">
        <p role="alert" className="lead-forms__error">Não foi possível carregar as respostas do formulário.</p>
      </section>
    );
  }

  if (state.items.length === 0) return null;

  return (
    <section className={className} data-testid="lead-forms">
      {variant === 'dossier' ? <h4 className="dossier-group__title">Respostas do formulário</h4> : <h3><FiFileText aria-hidden /> Respostas do formulário</h3>}
      {state.items.map(item => (
        <div key={item.eventId} className="lead-forms__item">
          <p className="lead-forms__source opp-panel__muted" data-testid={`lead-form-source-${item.eventId}`}>{sourceLine(item)}</p>
          {item.redacted && item.answers.length === 0 ? (
            <p className="lead-forms__redacted opp-panel__muted">As respostas foram removidas após o prazo de guarda.</p>
          ) : (
            <dl className="opp-panel__facts lead-forms__answers">
              {item.answers.map((a, i) => (
                <React.Fragment key={`${item.eventId}-${i}`}>
                  <dt>{a.label}</dt>
                  <dd>{a.value}</dd>
                </React.Fragment>
              ))}
            </dl>
          )}
        </div>
      ))}
    </section>
  );
};

export default LeadFormAnswers;
